import { AppOverlayService } from '@qsp-ui-library/services/modal-services/app-overlay/app-overlay.service';
import { AppSnackbarService } from '@qsp-ui-library/services/modal-services/app-snackbar/app-snackbar.service';
import { AppAuthenticationService } from '@qsp-ui-library/services/user-services/app-authentication/app-authentication.service';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EQimaSnackbarType } from '@qima/ngx-qima';
import { Observable, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private _isLoginExpiredShown = false;

  public constructor(
    private readonly _overlayService: AppOverlayService,
    private readonly _appSnackbarService: AppSnackbarService,
    private readonly _authenticationService: AppAuthenticationService
  ) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse): Observable<never> => {
        if (error.status === 401) {
          // step1:check req is reshresh token or not,logout the user if it is refresh token
          if (req.url.includes('/v1.0/security/azure/token') && req.method === 'PUT') {
            if (!this._isLoginExpiredShown) {
              this._isLoginExpiredShown = true;
              this._appSnackbarService.showSnackbar('COMMON.LOGIN_EXPIRED', EQimaSnackbarType.ERROR, 5000);

              timer(3000).subscribe((): void => {
                this._authenticationService.logout();
              });
            }
          } else if (req.url.includes('/v1.0/security/azure/token') && req.method === 'DELETE') {
            // do nothing
          } else {
            // step2: if it is not refresh token then refresh the token
            this._authenticationService.refreshAccessToken();
          }
        } else {
          // error.code == 4200 --data version conflict ,custom the message on biz page
          // 403 is not handled here, it will be popup message.
          const parsedError = this._parseErrorResponse(error);
          const errorCode = parsedError?.code;

          if (errorCode !== 4200 && (!req.headers.has('skipErrorHandler') || req.headers.get('skipErrorHandler') === 'false')) {
            const errorMessage = this._getErrorMessage(parsedError);

            if (req.headers.has('reqCode') && error.status === parseInt(req.headers.get('reqCode') as string, 10)) {
              this._appSnackbarService.showSnackbar(req.headers.get('errorMessage') || errorMessage || 'COMMON.SERVICE_ERROR', EQimaSnackbarType.ERROR, 5000);
            } else {
              // show error message from backend, if empty, show default message
              this._appSnackbarService.showSnackbar(errorMessage || 'COMMON.SERVICE_ERROR', EQimaSnackbarType.ERROR, 5000);
            }
          }

          this._overlayService.updateIsLoading(false);
          throw error;
        }

        this._overlayService.updateIsLoading(false);

        return new Observable<never>();
      })
    );
  }

  /**
   * Parse error response to handle both object and text (JSON string) formats
   * When responseType is 'text', error.error is a string that may contain JSON
   * @param {HttpErrorResponse} error - The HTTP error response
   * @returns {Record<string, unknown> | null} Parsed error object or null
   * @private
   */
  private _parseErrorResponse(error: HttpErrorResponse): Record<string, unknown> | null {
    if (!error.error) {
      return null;
    }

    // If error.error is already an object, return it directly
    if (typeof error.error === 'object' && error.error !== null) {
      return error.error as Record<string, unknown>;
    }

    // If error.error is a string, try to parse it as JSON
    if (typeof error.error === 'string') {
      try {
        return JSON.parse(error.error) as Record<string, unknown>;
      } catch {
        // If parsing fails, return null (will fall back to default error message)
        return null;
      }
    }

    return null;
  }

  /**
   * Extract error message from parsed error object
   * @param {Record<string, unknown> | null} parsedError - Parsed error object
   * @returns {string | undefined} Error message string or undefined
   * @private
   */
  private _getErrorMessage(parsedError: Record<string, unknown> | null): string | undefined {
    if (!parsedError) {
      return undefined;
    }

    const { message, msg } = parsedError;

    if (typeof message === 'string' && message) {
      return message;
    }

    if (typeof msg === 'string' && msg) {
      return msg;
    }

    return undefined;
  }
}
